import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { ButtonComponent, SlashContainer } from '@zolteam/axenergie-ui-library';
import { useRouter } from 'next/router';
import { useMeasure } from 'react-use';
// Constants
import Link from 'next/link';
import strings from '../../constants/Strings';
// Components
import Menu from '../../components/Molecules/Menu';
import HeaderWithSlashContainer from '../../components/Atoms/HeaderWithSlashContainer';
import Footer from '../../components/Molecules/Footer';
import SearchEngine from '../../components/Organisms/SearchEngine';
import ReassuranceBanner from '../../components/Molecules/ReassuranceBanner';
// Services and hooks
import menuAndFooterService from '../../services/contentful/menuAndFooterService';
import useIsMobile from '../../hooks/useIsMobile';
import AgencyService from '../../services/api/AgencyService';
import searchEngineUtils from '../../utils/searchEngineUtils';
// Utils
import utils from '../../utils/formatCMS';
import headUtils from '../../utils/headUtils';
// Images
import logo from '../../public/Images/logo.webp';
import arrow from '../../public/icons/arrow-down.svg';
import colors from '../../constants/Colors';

const AdviceDetail = (props) => {
  const [isMobile] = useIsMobile();
  const router = useRouter();
  const [videoUrlRef, { width: videoUrlWidth }] = useMeasure();
  const [contentBlockRef, { height: contentBlockHeight }] = useMeasure();

  const parseYTUrl = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : false;
  };

  const titleTag = headUtils.formatPageTitle(props.content.title);
  const descriptionTag = headUtils.formatMetaDescription(props.content.content);

  return (
    <div>
      <Head>
        <title>{titleTag}</title>
        <meta name="description" content={descriptionTag} />
        <meta property="og:title" content={titleTag} />
        <meta property="og:description" content={descriptionTag} />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_IMAGE_PREFIX_URL}/public/${props.content.illustrationFilePath}`}
        />
        <meta
          property="og:image:secure_url"
          content={`${process.env.NEXT_PUBLIC_IMAGE_PREFIX_URL}/public/${props.content.illustrationFilePath}`}
        />
        {/* eslint-disable-next-line @next/next/no-sync-scripts */}
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places`}
        />
      </Head>
      {/* header */}
      <div className="full-width d-flex f-row relative">
        {/* Menu */}
        <div className="absolute absolute-right-top-corner index-2">
          <Menu items={utils.formatCMSDatas('menu', props)} fontColor="grey-800-text" />
        </div>
        <HeaderWithSlashContainer
          imageUrl={`${process.env.NEXT_PUBLIC_IMAGE_PREFIX_URL}/public/${props.content.illustrationFilePath}`}
          news
        >
          {isMobile ? null
            : (
              <Link href="/" className="mt-3 mb-4">
                <a><Image alt="logo" src={logo} width={206} height={206} /></a>
              </Link>
            )}
          <div className={`${isMobile ? 'mv-2 pl-3' : 'mb-6 mt-4 pl-5'}`}>
            <ButtonComponent onClick={() => router.back()} theme="grey">
              <div className="d-flex justify-center align-center">
                <Image
                  alt="back-arrow"
                  className="rotate-90"
                  src={arrow}
                  width={20}
                />
                <span className="normal-weight">{strings.back}</span>
              </div>
            </ButtonComponent>
            <h1 className="grey-800-text medium-weight">{props.content.title}</h1>
          </div>
        </HeaderWithSlashContainer>
      </div>
      {/* Content */}
      {/* Background grey slash */}
      <div
        className="relative full-width white-background d-flex justify-center align-center"
        style={{ height: contentBlockHeight + 128 }}
      >
        <div className="absolute absolute-left-top-corner full-width">
          {isMobile ? null : (
            <SlashContainer width="41%" backgroundColor={colors.grey100} offset={20}>
              <div style={{ height: contentBlockHeight + 128 }} />
            </SlashContainer>
          )}
        </div>
        <div ref={contentBlockRef} className="absolute fgIndex" style={{ width: isMobile ? '100%' : '80%', top: 0 }}>
          {props.content.videoUrl ? (
            <div
              ref={videoUrlRef}
              className="mh-3 mt-5 mb-2"
              style={{
                position: 'relative',
                height: Math.round((9 / 16) * videoUrlWidth),
                maxWidth: 1000,
              }}
            >
              <iframe
                className="absolute full-width full-height"
                style={{
                  top: 0,
                  left: 0,
                }}
                src={`https://www.youtube.com/embed/${parseYTUrl(props.content.videoUrl)}`}
                frameBorder="0"
                allowFullScreen
                title="agency-video"
              />
            </div>
          ) : null}
          <div
            style={{ maxWidth: 1000 }}
            className="grey-600-text ph-3 wysiwyg-text ws-pre-wrap"
            dangerouslySetInnerHTML={{ __html: props.content.content }}
          />
        </div>
      </div>
      {/* Search Engine */}
      <div className={`grey-800-background 
      ${isMobile ? 'ph-2 pv-4' : 'p-5'} full-width d-flex justify-center align-center f-column`}
      >
        <p className="h2 white-text medium-weight m-0 text-align-center">{strings.findYourHeatingPro}</p>
        <p className={`grey-400-text normal-weight text-align-center ${isMobile ? 'ph-2' : 'ph-6'} mv-4`}>
          {strings.findYourHeatingProSubtitle}
        </p>
        <div className="d-flex justify-center align-center white-background radius-5">
          <SearchEngine
            theme="light"
            withLogo={false}
            withLabel
            handleSubmit={(data) => searchEngineUtils.goToSearchPage(router, data)}
          />
        </div>
      </div>
      {/* Reassurance banner */}
      <ReassuranceBanner items={utils.formatCMSReassurance(props)} />
      {/* Footer */}
      <Footer
        networkBtn={props.footerRseauLienBouton}
        clientItems={utils.formatCMSDatas('footerClient', props)}
        networkItems={utils.formatCMSDatas('footerRseau', props)}
        facebook={props.footerFacebook}
        instagram={props.footerInstagram}
        twitter={props.footerTwitter}
        mentions={props.mentions}
        legalItems={utils.formatCMSDatas('sousFooter', props)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default AdviceDetail;

export async function getServerSideProps({ params }) {
  let menuAndFooter;
  let content;
  try {
    menuAndFooter = await menuAndFooterService.menuAndFooter();
  } catch (err) {
    menuAndFooter = {};
    console.error(err);
  }
  try {
    content = await AgencyService.getOneAgencyPageContent(params?.id?.split('-')?.[0]);
  } catch (err) {
    content = {};
    console.error(err);
  }
  if (!content?.data) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
  return {
    props: {
      ...menuAndFooter?.fields,
      content: content?.data,
    },
  };
}
